/**
 * Advanced Analytics Dashboard - Business Intelligence
 * Revenue, users, drivers, delivery metrics with charts
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Users,
  Truck,
  DollarSign,
  Package,
  BarChart3,
  RefreshCw,
  Calendar,
  Star,
  Target,
  Activity
} from "lucide-react";

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState(30);
  const [data, setData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [driverStats, setDriverStats] = useState(null);
  const [deliveryStats, setDeliveryStats] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [overview, revenue, users, drivers, delivery] = await Promise.all([
        api.get("/analytics/overview?days=" + period),
        api.get("/analytics/revenue?days=" + period),
        api.get("/analytics/users?days=" + period),
        api.get("/analytics/drivers?days=" + period),
        api.get("/analytics/delivery?days=" + period)
      ]);
      
      setData(overview.data);
      setRevenueData(revenue.data);
      setUserStats(users.data);
      setDriverStats(drivers.data);
      setDeliveryStats(delivery.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const formatCurrency = (amount) => {
    return "J$" + (amount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950" data-testid="analytics-dashboard">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 px-4 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin")}
              className="p-2 text-slate-400 hover:text-white"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <BarChart3 className="h-6 w-6 text-blue-400" />
                Analytics Dashboard
              </h1>
              <p className="text-sm text-slate-400">Business intelligence & metrics</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={period}
              onChange={(e) => setPeriod(Number(e.target.value))}
              className="bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
            <Button
              variant="ghost"
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 text-slate-400"
            >
              <RefreshCw className={"h-5 w-5 " + (refreshing ? "animate-spin" : "")} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPICard
            title="Total Revenue"
            value={formatCurrency(data?.revenue?.total)}
            subtitle={"Avg " + formatCurrency(data?.revenue?.average_order) + "/order"}
            icon={DollarSign}
            color="emerald"
          />
          <KPICard
            title="Orders"
            value={data?.orders?.total || 0}
            subtitle={data?.orders?.completion_rate?.toFixed(1) + "% completed"}
            icon={Package}
            color="blue"
          />
          <KPICard
            title="Users"
            value={data?.users?.total || 0}
            subtitle={"+" + (data?.users?.new || 0) + " new"}
            icon={Users}
            color="purple"
          />
          <KPICard
            title="Deliveries"
            value={data?.delivery?.total || 0}
            subtitle={data?.delivery?.success_rate?.toFixed(1) + "% success"}
            icon={Truck}
            color="amber"
          />
        </div>

        {/* Revenue Chart */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-400" />
            Revenue Trend
          </h2>
          <div className="h-48 flex items-end gap-1">
            {revenueData?.daily_breakdown?.slice(-14).map((day, idx) => {
              const maxRev = Math.max(...(revenueData?.daily_breakdown || []).map(d => d.revenue)) || 1;
              const height = (day.revenue / maxRev) * 100;
              return (
                <div key={idx} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-emerald-500 rounded-t hover:bg-emerald-400 transition-colors cursor-pointer"
                    style={{ height: Math.max(4, height) + "%" }}
                    title={formatCurrency(day.revenue) + " on " + day.date}
                  />
                  <span className="text-xs text-slate-500 mt-1 rotate-45 origin-left">
                    {day.date?.slice(5)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Top Products */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-400" />
              Top Products
            </h2>
            <div className="space-y-3">
              {revenueData?.top_products?.slice(0, 5).map((product, idx) => (
                <div key={product.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-slate-500 text-sm w-5">{idx + 1}.</span>
                    <span className="text-white truncate max-w-[150px]">{product.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-400 font-medium">{formatCurrency(product.revenue)}</p>
                    <p className="text-xs text-slate-500">{product.quantity} sold</p>
                  </div>
                </div>
              ))}
              {(!revenueData?.top_products || revenueData.top_products.length === 0) && (
                <p className="text-slate-500 text-center py-4">No product data available</p>
              )}
            </div>
          </div>

          {/* User Stats */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-400" />
              User Breakdown
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Total Users</span>
                <span className="text-white font-bold text-xl">{userStats?.total_users || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Active Users</span>
                <span className="text-emerald-400 font-medium">{userStats?.active_users || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">New Signups</span>
                <span className="text-blue-400 font-medium">+{userStats?.new_users || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Retention Rate</span>
                <span className="text-amber-400 font-medium">{userStats?.retention_rate?.toFixed(1) || 0}%</span>
              </div>
              <div className="pt-4 border-t border-slate-800">
                <p className="text-sm text-slate-500 mb-2">By Role:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(userStats?.by_role || {}).map(([role, count]) => (
                    <span key={role} className="px-2 py-1 bg-slate-800 rounded text-xs text-slate-300">
                      {role}: {count}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Driver Performance */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Truck className="h-5 w-5 text-cyan-400" />
              Driver Performance
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{driverStats?.active_drivers || 0}</p>
                  <p className="text-xs text-slate-400">Active Drivers</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{driverStats?.average_per_driver?.toFixed(1) || 0}</p>
                  <p className="text-xs text-slate-400">Avg Deliveries</p>
                </div>
              </div>
              <div className="pt-4 border-t border-slate-800">
                <p className="text-sm text-slate-500 mb-2">Top Performers:</p>
                <div className="space-y-2">
                  {driverStats?.top_performers?.slice(0, 3).map((driver, idx) => (
                    <div key={driver.driver_id} className="flex justify-between items-center">
                      <span className="text-slate-300">Driver #{idx + 1}</span>
                      <span className="text-emerald-400">{driver.delivered} deliveries</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Stats */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Target className="h-5 w-5 text-red-400" />
              Delivery Metrics
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Success Rate</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-500" 
                      style={{ width: (deliveryStats?.success_rate || 0) + "%" }}
                    />
                  </div>
                  <span className="text-emerald-400 font-medium">{deliveryStats?.success_rate?.toFixed(1) || 0}%</span>
                </div>
              </div>
              <div className="pt-4 border-t border-slate-800">
                <p className="text-sm text-slate-500 mb-2">By Status:</p>
                <div className="space-y-2">
                  {Object.entries(deliveryStats?.by_status || {}).map(([status, count]) => (
                    <div key={status} className="flex justify-between items-center">
                      <span className="text-slate-400 capitalize">{status.replace(/_/g, " ")}</span>
                      <span className="text-white">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="pt-4 border-t border-slate-800">
                <p className="text-sm text-slate-500 mb-2">By Type:</p>
                <div className="flex gap-4">
                  {Object.entries(deliveryStats?.by_type || {}).map(([type, count]) => (
                    <div key={type} className="bg-slate-800 rounded px-3 py-2 text-center flex-1">
                      <p className="text-lg font-bold text-white">{count}</p>
                      <p className="text-xs text-slate-400 capitalize">{type.replace(/_/g, " ")}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, color }) => {
  const colors = {
    emerald: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    blue: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    purple: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    amber: "bg-amber-500/20 text-amber-400 border-amber-500/30"
  };

  return (
    <div className={"rounded-xl p-4 border " + colors[color]}>
      <div className="flex items-center gap-3 mb-2">
        <Icon className="h-5 w-5" />
        <span className="text-sm opacity-80">{title}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-xs opacity-60 mt-1">{subtitle}</p>
    </div>
  );
};

export default AnalyticsDashboard;
